import React from 'react';
import { FadeInUp, SlideInLeft, SectionLabel, AccentLine, StaggerContainer, StaggerItem } from './AnimationWrappers';
import { Shield, Leaf, Globe, Heart, Eye, Recycle, Award, BarChart3 } from 'lucide-react';

const CERTS = [
  { code: 'ISO 9001:2015', title: 'Quality Management', desc: 'International standard for quality management systems ensuring consistent product and service delivery.', icon: Award, category: 'Quality' },
  { code: 'ISO 14001:2015', title: 'Environmental Management', desc: 'Framework for effective environmental management systems to reduce ecological footprint.', icon: Leaf, category: 'Environment' },
  { code: 'ISO 45001:2018', title: 'Occupational Health & Safety', desc: 'Global standard for workplace safety, preventing work-related injury and ill health.', icon: Shield, category: 'Safety' },
  { code: 'ISO 14064-1:2018', title: 'Greenhouse Gas Accounting', desc: 'Specification for the quantification and reporting of greenhouse gas emissions and removals at the organizational level.', icon: BarChart3, category: 'Carbon' },
  { code: 'SA 8000', title: 'Social Accountability', desc: 'International certification standard encouraging organizations to develop and maintain decent workplace practices.', icon: Heart, category: 'Social' },
  { code: 'SEDEX SMETA', title: 'Ethical Trade Audit', desc: 'The most widely used social audit methodology in the world for ethical trade compliance.', icon: Eye, category: 'Ethics' },
  { code: 'GOTS', title: 'Global Organic Standard', desc: 'Leading worldwide processing standard for textiles made from organic fibres.', icon: Globe, category: 'Organic' },
  { code: 'GRS', title: 'Global Recycled Standard', desc: 'Full product standard for tracking and verifying recycled content in final products.', icon: Recycle, category: 'Recycling' },
];

const ComplianceSection = () => {
  return (
    <section id="compliance" data-testid="compliance-section" className="relative overflow-hidden" style={{ background: '#F5F0EB', padding: '40px 0 30px' }}>
      <div className="container-main">
        <SlideInLeft>
          <SectionLabel number="03" title="Compliance" />
        </SlideInLeft>
        <FadeInUp>
          <AccentLine />
          <h2 className="font-display mb-2 md:mb-3" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.4rem)', fontWeight: 700, letterSpacing: '-0.02em', color: '#1C1C1E' }}>
            Certified. Audited. <span style={{ color: '#C8102E' }}>Verified.</span>
          </h2>
          <p className="font-body mb-6 md:mb-12 hidden md:block" style={{ fontSize: '1.05rem', color: '#6B6B6F' }}>
            Every claim backed by international standards. Every process open to scrutiny.
          </p>
        </FadeInUp>

        {/* Mobile: compact 2-col badge grid */}
        <div className="md:hidden grid grid-cols-2 gap-2">
          {CERTS.map((cert, i) => (
            <div
              key={i}
              data-testid={`cert-${i}`}
              className="flex items-start gap-2.5 p-3"
              style={{ background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E8E0D5' }}
            >
              <div className="shrink-0 mt-0.5" style={{
                width: '28px', height: '28px', borderRadius: '6px',
                background: 'rgba(200, 16, 46, 0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <cert.icon size={14} color="#C8102E" strokeWidth={1.5} />
              </div>
              <div>
                <span className="font-display block" style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1C1C1E', lineHeight: 1.2 }}>{cert.code}</span>
                <span className="font-body" style={{ fontSize: '0.6rem', color: '#6B6B6F', lineHeight: 1.3 }}>{cert.title}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop: full card layout */}
        <div className="hidden md:block">
          <StaggerContainer>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              {CERTS.slice(0, 4).map((cert, i) => (
                <StaggerItem key={i}>
                  <div
                    data-testid={`cert-desktop-${i}`}
                    className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    style={{
                      background: '#FFFFFF',
                      borderRadius: '12px',
                      border: '1px solid #D4CFC7',
                      padding: '2rem 1.5rem',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                      minHeight: '220px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div className="absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: '#C8102E' }} />
                    <div className="flex items-start justify-between mb-4">
                      <div style={{
                        width: '44px', height: '44px', borderRadius: '10px',
                        background: 'rgba(200, 16, 46, 0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <cert.icon size={20} color="#C8102E" strokeWidth={1.5} />
                      </div>
                      <span className="font-mono" style={{
                        fontSize: '0.5rem', fontWeight: 600, letterSpacing: '0.1em',
                        color: '#C8102E', background: 'rgba(200, 16, 46, 0.06)',
                        padding: '4px 10px', borderRadius: '4px', textTransform: 'uppercase',
                      }}>
                        {cert.category}
                      </span>
                    </div>
                    <span className="font-display block mb-1" style={{
                      fontSize: '1.6rem', fontWeight: 700, color: '#1C1C1E',
                      letterSpacing: '-0.01em', lineHeight: 1.1,
                    }}>
                      {cert.code}
                    </span>
                    <h3 className="font-body mb-2" style={{
                      fontSize: '0.88rem', fontWeight: 600, color: '#3A3A3C', lineHeight: 1.3,
                    }}>
                      {cert.title}
                    </h3>
                    <p className="font-body mt-auto" style={{
                      fontSize: '0.78rem', color: '#6B6B6F', lineHeight: 1.6,
                    }}>
                      {cert.desc}
                    </p>
                  </div>
                </StaggerItem>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {CERTS.slice(4).map((cert, i) => (
                <StaggerItem key={i + 4}>
                  <div
                    data-testid={`cert-desktop-${i + 4}`}
                    className="group relative overflow-hidden transition-all duration-300 hover:-translate-y-1"
                    style={{
                      background: '#FFFFFF',
                      borderRadius: '12px',
                      border: '1px solid #D4CFC7',
                      padding: '1.5rem',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                    }}
                  >
                    <div className="absolute top-0 left-0 right-0 h-[3px] opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: '#C8102E' }} />
                    <div className="flex items-center gap-3 mb-3">
                      <div style={{
                        width: '36px', height: '36px', borderRadius: '8px',
                        background: 'rgba(200, 16, 46, 0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <cert.icon size={16} color="#C8102E" strokeWidth={1.5} />
                      </div>
                    </div>
                    <span className="font-display block mb-1" style={{
                      fontSize: '1.25rem', fontWeight: 700, color: '#1C1C1E',
                      lineHeight: 1.1,
                    }}>
                      {cert.code}
                    </span>
                    <h3 className="font-body mb-1.5" style={{
                      fontSize: '0.82rem', fontWeight: 600, color: '#3A3A3C', lineHeight: 1.3,
                    }}>
                      {cert.title}
                    </h3>
                    <p className="font-body" style={{
                      fontSize: '0.72rem', color: '#6B6B6F', lineHeight: 1.55,
                    }}>
                      {cert.desc}
                    </p>
                  </div>
                </StaggerItem>
              ))}
            </div>
          </StaggerContainer>

          <FadeInUp delay={0.3}>
            <div className="mt-10 flex flex-wrap items-center justify-between gap-6 pt-6" style={{ borderTop: '1px solid #D4CFC7' }}>
              <p className="font-body" style={{ fontSize: '0.88rem', color: '#6B6B6F', maxWidth: '500px' }}>
                All certifications are current and audited annually by independent third-party assessors.
              </p>
              <div className="flex items-center gap-6">
                {[
                  { num: '8', label: 'Active Certifications' },
                  { num: '100%', label: 'Audit Pass Rate' },
                ].map((stat, i) => (
                  <div key={i} className="text-right">
                    <span className="font-display block" style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1C1C1E' }}>{stat.num}</span>
                    <span className="font-mono" style={{ fontSize: '0.55rem', color: '#6B6B6F', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </FadeInUp>
        </div>
      </div>
      <div className="section-number">03</div>
    </section>
  );
};

export default ComplianceSection;
